import { AdminUsers, StorymakerUsers } from './constants';

type RoleCheck = (roleInfo: UserRoleInfo) => boolean;

const ADMINISTRATOR_ROLES: UserRole[] = [AdminUsers.AccountAdmin];

export const MANAGER_TIER_ROLES: UserRole[] = [
  AdminUsers.Manager,
  AdminUsers.Moderator2,
  AdminUsers.ModeratorSecure2,
];

export const isPrimary: RoleCheck = (roleInfo: UserRoleInfo) => roleInfo.isPrimary;

export const isManager = (roleInfo: UserRoleInfo | UserRole) =>
  (typeof roleInfo === 'string' ? roleInfo : roleInfo.role) === AdminUsers.Manager;

export const isManagerTier = (roleInfo: UserRoleInfo | UserRole | undefined | null) =>
  MANAGER_TIER_ROLES.includes(typeof roleInfo === 'string' ? roleInfo : roleInfo?.role);

// note the `roleInfo` arg can be undefined sometimes (after browser reload or HMR)
export const isAdministrator = (roleInfo: UserRoleInfo | UserRole) =>
  isAdministratorRole(typeof roleInfo === 'string' ? roleInfo : roleInfo?.role);

export const isAdministratorOrNonNowManager: RoleCheck = (roleInfo: UserRoleInfo | UserRole) => {
  const userRole = typeof roleInfo === 'string' ? roleInfo : roleInfo.role;
  return (
    isAdministratorRole(userRole) || (userRole !== AdminUsers.Manager && !isReviewerRole(userRole))
  );
};

export const isAdministratorRole = (role?: UserRole) => ADMINISTRATOR_ROLES.includes(role);

export const isReviewerRole = (role?: UserRole) => role === StorymakerUsers.Reviewer;

export const isNotReviewer = (roleInfo: UserRoleInfo | UserRole) =>
  !isReviewerRole(typeof roleInfo === 'string' ? roleInfo : roleInfo?.role);

export const canAccessReportOrganizationData = (role?: UserRole) =>
  isAdministratorRole(role) || role === AdminUsers.Superadmin;

/**
 * Who may move an organisation to the Recycle Bin or restore it. Account Admin only, by
 * decision: Superadmin is a legacy CMS 1.0 role and is treated as a customer here.
 */
export const canManageOrganizationLifecycleRole = (role?: UserRole) =>
  role === AdminUsers.AccountAdmin;

export const canManageOrganizationLifecycle = (roleInfo: UserRoleInfo | UserRole) =>
  canManageOrganizationLifecycleRole(typeof roleInfo === 'string' ? roleInfo : roleInfo?.role);

export const canAccessUtilizationReports = (role?: UserRole) =>
  canAccessReportOrganizationData(role) || MANAGER_TIER_ROLES.includes(role);

export const isAnyAdminRole = (role?: UserRole) => Object.values(AdminUsers).includes(role);

export const validateRoles = (checks: RoleCheck[], roleInfo: UserRoleInfo) =>
  checks.every((check) => check(roleInfo));

export const and = <T>(...args: T[]) => args.flat();

export const shouldRequireRobustPassword = (role?: UserRole | null) =>
  role === AdminUsers.ModeratorSecure ||
  role === AdminUsers.ModeratorSecure2 ||
  role === AdminUsers.AccountAdmin;

const exports = {
  isPrimary: [isPrimary],
  isManager: [isManager],
  isAdministrator: [isAdministrator],
  isAdministratorOrNonNowManager: [isAdministratorOrNonNowManager],
  canManageOrganizationLifecycle: [canManageOrganizationLifecycle],
  isNotReviewer: [isNotReviewer],
};

export default exports;
